import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toaster'
import { cn } from '@/lib/utils'
import {
    ArrowLeft,
    Plus,
    Trash2,
    GripVertical,
    LayoutGrid,
    List,
    Loader2,
    MoreHorizontal,
    Edit2,
    Settings,
} from 'lucide-react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'

interface Beat {
    id: string
    title: string
    description: string | null
    act: number | null
    sequence: number | null
    positionX: number
    positionY: number
    sortOrder: number
    color: string
    status: 'outline' | 'draft' | 'revised' | 'final'
}

interface Act {
    id: string
    name: string
    color: string
}

interface BeatBoard {
    id: string
    name: string
    description: string | null
    structure: {
        acts: Act[]
        viewMode: 'board' | 'list'
        zoom: number
    } | null
    beats: Beat[]
}

const ACT_COLORS = [
    '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#22c55e',
    '#14b8a6', '#f97316', '#ec4899', '#6366f1', '#84cc16',
]

const BEAT_COLORS = [
    '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e',
    '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6',
]

export default function BeatBoardPage() {
    const { projectId, beatBoardId } = useParams<{ projectId: string; beatBoardId: string }>()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const [viewMode, setViewMode] = useState<'board' | 'list'>('board')
    const [isCreateBeatOpen, setIsCreateBeatOpen] = useState(false)
    const [isCreateActOpen, setIsCreateActOpen] = useState(false)
    const [editingBeat, setEditingBeat] = useState<Beat | null>(null)
    const [editingAct, setEditingAct] = useState<Act | null>(null)
    const [newBeatTitle, setNewBeatTitle] = useState('')
    const [newBeatDescription, setNewBeatDescription] = useState('')
    const [newBeatAct, setNewBeatAct] = useState<number>(1)
    const [newBeatColor, setNewBeatColor] = useState(BEAT_COLORS[0])
    const [newActName, setNewActName] = useState('')
    const [newActColor, setNewActColor] = useState(ACT_COLORS[0])
    const [draggedBeat, setDraggedBeat] = useState<Beat | null>(null)

    const { data: boardData, isLoading } = useQuery({
        queryKey: ['beat-board', beatBoardId],
        queryFn: () => api.get<BeatBoard>(`/projects/${projectId}/beat-boards/${beatBoardId}`),
        enabled: !!beatBoardId,
    })

    const updateBoardMutation = useMutation({
        mutationFn: (data: { structure: string }) =>
            api.patch(`/projects/${projectId}/beat-boards/${beatBoardId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['beat-board', beatBoardId] })
        },
    })

    const createBeatMutation = useMutation({
        mutationFn: (data: { title: string; description?: string; act?: number; color?: string }) =>
            api.post(`/projects/${projectId}/beat-boards/${beatBoardId}/beats`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['beat-board', beatBoardId] })
            setIsCreateBeatOpen(false)
            resetBeatForm()
            toast({ title: 'Beat created' })
        },
        onError: () => {
            toast({ title: 'Failed to create beat', variant: 'destructive' })
        },
    })

    const updateBeatMutation = useMutation({
        mutationFn: ({ beatId, data }: { beatId: string; data: Partial<Beat> }) =>
            api.patch(`/projects/${projectId}/beat-boards/${beatBoardId}/beats/${beatId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['beat-board', beatBoardId] })
            setEditingBeat(null)
            resetBeatForm()
        },
    })

    const deleteBeatMutation = useMutation({
        mutationFn: (beatId: string) =>
            api.delete(`/projects/${projectId}/beat-boards/${beatBoardId}/beats/${beatId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['beat-board', beatBoardId] })
            toast({ title: 'Beat deleted' })
        },
    })

    const resetBeatForm = () => {
        setNewBeatTitle('')
        setNewBeatDescription('')
        setNewBeatAct(1)
        setNewBeatColor(BEAT_COLORS[0])
    }

    const resetActForm = () => {
        setNewActName('')
        setNewActColor(ACT_COLORS[0])
    }

    const handleCreateBeat = (e: React.FormEvent) => {
        e.preventDefault()
        createBeatMutation.mutate({
            title: newBeatTitle,
            description: newBeatDescription || undefined,
            act: newBeatAct,
            color: newBeatColor,
        })
    }

    const handleUpdateBeat = (e: React.FormEvent) => {
        e.preventDefault()
        if (!editingBeat) return
        updateBeatMutation.mutate({
            beatId: editingBeat.id,
            data: {
                title: newBeatTitle,
                description: newBeatDescription || null,
                act: newBeatAct,
                color: newBeatColor,
            },
        })
    }

    const handleCreateAct = (e: React.FormEvent) => {
        e.preventDefault()
        if (!board) return

        const newAct: Act = {
            id: `act-${Date.now()}`,
            name: newActName,
            color: newActColor,
        }

        const currentActs = board.structure?.acts || []
        const newStructure = {
            ...board.structure,
            acts: [...currentActs, newAct],
            viewMode: board.structure?.viewMode || 'board',
            zoom: board.structure?.zoom || 1,
        }

        updateBoardMutation.mutate(
            { structure: JSON.stringify(newStructure) },
            {
                onSuccess: () => {
                    setIsCreateActOpen(false)
                    resetActForm()
                    toast({ title: 'Act created' })
                },
            }
        )
    }

    const handleUpdateAct = (e: React.FormEvent) => {
        e.preventDefault()
        if (!editingAct || !board) return

        const currentActs = board.structure?.acts || []
        const newStructure = {
            ...board.structure,
            acts: currentActs.map(act =>
                act.id === editingAct.id
                    ? { ...act, name: newActName, color: newActColor }
                    : act
            ),
            viewMode: board.structure?.viewMode || 'board',
            zoom: board.structure?.zoom || 1,
        }

        updateBoardMutation.mutate(
            { structure: JSON.stringify(newStructure) },
            {
                onSuccess: () => {
                    setEditingAct(null)
                    resetActForm()
                    toast({ title: 'Act updated' })
                },
            }
        )
    }

    const handleDeleteAct = (actId: string) => {
        if (!board) return

        const currentActs = board.structure?.acts || []
        const newStructure = {
            ...board.structure,
            acts: currentActs.filter(act => act.id !== actId),
            viewMode: board.structure?.viewMode || 'board',
            zoom: board.structure?.zoom || 1,
        }

        updateBoardMutation.mutate(
            { structure: JSON.stringify(newStructure) },
            {
                onSuccess: () => {
                    toast({ title: 'Act deleted' })
                },
            }
        )
    }

    const openEditBeatDialog = (beat: Beat) => {
        setEditingBeat(beat)
        setNewBeatTitle(beat.title)
        setNewBeatDescription(beat.description || '')
        setNewBeatAct(beat.act || 1)
        setNewBeatColor(beat.color)
    }

    const openEditActDialog = (act: Act) => {
        setEditingAct(act)
        setNewActName(act.name)
        setNewActColor(act.color)
    }

    const handleDragStart = (beat: Beat) => {
        setDraggedBeat(beat)
    }

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault()
    }

    const handleDrop = (e: React.DragEvent, targetBeat: Beat, actNumber: number) => {
        e.preventDefault()
        if (!draggedBeat || draggedBeat.id === targetBeat.id) return

        updateBeatMutation.mutate({
            beatId: draggedBeat.id,
            data: {
                act: actNumber,
                sortOrder: targetBeat.sortOrder,
            },
        })
        setDraggedBeat(null)
    }

    const board = boardData?.data
    const acts = board?.structure?.acts || []

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        )
    }

    if (!board) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <p className="text-muted-foreground mb-4">Beat board not found</p>
                <Button onClick={() => navigate(`/projects/${projectId}`)}>Back to Project</Button>
            </div>
        )
    }

    const beatsByAct = acts.map((act, index) => ({
        ...act,
        actNumber: index + 1,
        beats: board.beats.filter(b => b.act === index + 1).sort((a, b) => a.sortOrder - b.sortOrder),
    }))

    // Beats without an act assignment
    const unassignedBeats = board.beats.filter(b => !b.act || b.act > acts.length)

    return (
        <div className="flex flex-col h-[calc(100vh-8rem)]">
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/projects/${projectId}`)}>
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-xl font-bold">{board.name}</h1>
                        {board.description && (
                            <p className="text-sm text-muted-foreground">{board.description}</p>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex items-center border rounded-lg">
                        <button
                            onClick={() => setViewMode('board')}
                            className={cn(
                                "p-2 transition-colors",
                                viewMode === 'board' ? 'bg-accent' : 'hover:bg-accent/50'
                            )}
                        >
                            <LayoutGrid className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={cn(
                                "p-2 transition-colors",
                                viewMode === 'list' ? 'bg-accent' : 'hover:bg-accent/50'
                            )}
                        >
                            <List className="h-4 w-4" />
                        </button>
                    </div>

                    <Button variant="outline" size="sm" onClick={() => setIsCreateActOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Act
                    </Button>

                    <Button size="sm" onClick={() => setIsCreateBeatOpen(true)} disabled={acts.length === 0}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add Beat
                    </Button>
                </div>
            </div>

            {/* Empty State */}
            {acts.length === 0 && (
                <div className="flex-1 flex flex-col items-center justify-center text-center">
                    <div className="p-8 max-w-md">
                        <LayoutGrid className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <h2 className="text-xl font-semibold mb-2">Start Your Beat Board</h2>
                        <p className="text-muted-foreground mb-6">
                            Create Acts to organize your story structure. You can name them anything you like
                            — "Act 1", "Setup", "Rising Action", or any structure that works for your story.
                        </p>
                        <Button onClick={() => setIsCreateActOpen(true)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Create Your First Act
                        </Button>
                    </div>
                </div>
            )}

            {/* Board View */}
            {viewMode === 'board' && acts.length > 0 && (
                <div className="flex-1 overflow-auto py-4">
                    <div className="flex gap-4 min-w-max">
                        {beatsByAct.map((act) => (
                            <div
                                key={act.id}
                                className="w-80 flex-shrink-0"
                            >
                                {/* Act header */}
                                <div
                                    className="flex items-center justify-between p-3 rounded-t-lg text-white"
                                    style={{ backgroundColor: act.color }}
                                >
                                    <span className="font-semibold">{act.name}</span>
                                    <div className="flex items-center gap-1">
                                        <span className="text-sm opacity-80">{act.beats.length}</span>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 hover:bg-white/20"
                                            onClick={() => openEditActDialog(act)}
                                        >
                                            <Settings className="h-3 w-3" />
                                        </Button>
                                    </div>
                                </div>

                                {/* Beats column */}
                                <div className="bg-accent/30 rounded-b-lg p-3 min-h-[400px] space-y-2">
                                    {act.beats.map((beat) => (
                                        <div
                                            key={beat.id}
                                            draggable
                                            onDragStart={() => handleDragStart(beat)}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, beat, act.actNumber)}
                                            className={cn(
                                                "beat-card bg-card cursor-grab active:cursor-grabbing",
                                                draggedBeat?.id === beat.id && "opacity-50"
                                            )}
                                        >
                                            <div className="flex items-start gap-2">
                                                <GripVertical className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <div
                                                            className="w-2 h-2 rounded-full shrink-0"
                                                            style={{ backgroundColor: beat.color }}
                                                        />
                                                        <h4 className="font-medium text-sm truncate">{beat.title}</h4>
                                                    </div>
                                                    {beat.description && (
                                                        <p className="text-xs text-muted-foreground line-clamp-2">
                                                            {beat.description}
                                                        </p>
                                                    )}
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6 shrink-0"
                                                    onClick={() => openEditBeatDialog(beat)}
                                                >
                                                    <MoreHorizontal className="h-3 w-3" />
                                                </Button>
                                            </div>
                                            <div className="flex items-center justify-between mt-2 pt-2 border-t text-xs text-muted-foreground">
                                                <span className="capitalize">{beat.status}</span>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Add beat to act */}
                                    <button
                                        onClick={() => {
                                            setNewBeatAct(act.actNumber)
                                            setIsCreateBeatOpen(true)
                                        }}
                                        className="w-full p-3 border-2 border-dashed rounded-lg text-sm text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors"
                                    >
                                        <Plus className="h-4 w-4 mx-auto mb-1" />
                                        Add Beat
                                    </button>
                                </div>
                            </div>
                        ))}

                        {/* Add new act column */}
                        <div
                            onClick={() => setIsCreateActOpen(true)}
                            className="w-80 flex-shrink-0 min-h-[450px] border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-accent/20 transition-colors group"
                        >
                            <div className="text-center text-muted-foreground group-hover:text-primary">
                                <Plus className="h-8 w-8 mx-auto mb-2" />
                                <p className="font-medium">Add New Act</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* List View */}
            {viewMode === 'list' && acts.length > 0 && (
                <div className="flex-1 overflow-auto py-4">
                    <div className="space-y-6">
                        {beatsByAct.map((act) => (
                            <div key={act.id}>
                                <div className="flex items-center justify-between mb-2">
                                    <h3
                                        className="text-sm font-semibold flex items-center gap-2"
                                        style={{ color: act.color }}
                                    >
                                        <div className="w-3 h-3 rounded" style={{ backgroundColor: act.color }} />
                                        {act.name}
                                    </h3>
                                    <Button variant="ghost" size="sm" onClick={() => openEditActDialog(act)}>
                                        <Edit2 className="h-3 w-3" />
                                    </Button>
                                </div>
                                <div className="border rounded-lg divide-y">
                                    {act.beats.map((beat) => (
                                        <div key={beat.id} className="flex items-center gap-4 p-3 hover:bg-accent/50">
                                            <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                                            <div
                                                className="w-3 h-3 rounded shrink-0"
                                                style={{ backgroundColor: beat.color }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-medium">{beat.title}</h4>
                                                {beat.description && (
                                                    <p className="text-sm text-muted-foreground truncate">
                                                        {beat.description}
                                                    </p>
                                                )}
                                            </div>
                                            <span className="text-xs text-muted-foreground capitalize">{beat.status}</span>
                                            <Button variant="ghost" size="icon" onClick={() => openEditBeatDialog(beat)}>
                                                <Edit2 className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => deleteBeatMutation.mutate(beat.id)}
                                            >
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        </div>
                                    ))}
                                    {act.beats.length === 0 && (
                                        <div className="p-8 text-center text-muted-foreground">
                                            No beats in this act yet
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Unassigned beats */}
                        {unassignedBeats.length > 0 && (
                            <div>
                                <h3 className="text-sm font-semibold mb-2 text-muted-foreground">
                                    Unassigned Beats
                                </h3>
                                <div className="border rounded-lg divide-y">
                                    {unassignedBeats.map((beat) => (
                                        <div key={beat.id} className="flex items-center gap-4 p-3 hover:bg-accent/50">
                                            <div
                                                className="w-3 h-3 rounded shrink-0"
                                                style={{ backgroundColor: beat.color }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-medium">{beat.title}</h4>
                                            </div>
                                            <Button variant="ghost" size="icon" onClick={() => openEditBeatDialog(beat)}>
                                                <Edit2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Create/Edit Beat Dialog */}
            <Dialog open={isCreateBeatOpen || !!editingBeat} onOpenChange={(open) => {
                if (!open) {
                    setIsCreateBeatOpen(false)
                    setEditingBeat(null)
                    resetBeatForm()
                }
            }}>
                <DialogContent>
                    <form onSubmit={editingBeat ? handleUpdateBeat : handleCreateBeat}>
                        <DialogHeader>
                            <DialogTitle>{editingBeat ? 'Edit Beat' : 'Add New Beat'}</DialogTitle>
                            <DialogDescription>
                                {editingBeat ? 'Update this beat\'s details.' : 'Create a new beat for your story structure.'}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="beatTitle">Title</Label>
                                <Input
                                    id="beatTitle"
                                    placeholder="Beat title..."
                                    value={newBeatTitle}
                                    onChange={(e) => setNewBeatTitle(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="beatDescription">Description</Label>
                                <Input
                                    id="beatDescription"
                                    placeholder="What happens in this beat..."
                                    value={newBeatDescription}
                                    onChange={(e) => setNewBeatDescription(e.target.value)}
                                />
                            </div>
                            {acts.length > 0 && (
                                <div className="space-y-2">
                                    <Label>Act</Label>
                                    <div className="flex gap-2 flex-wrap">
                                        {acts.map((act, index) => (
                                            <Button
                                                key={act.id}
                                                type="button"
                                                variant={newBeatAct === index + 1 ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setNewBeatAct(index + 1)}
                                            >
                                                {act.name}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label>Color</Label>
                                <div className="flex gap-2 flex-wrap">
                                    {BEAT_COLORS.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => setNewBeatColor(color)}
                                            className={cn(
                                                "w-6 h-6 rounded-full transition-transform",
                                                newBeatColor === color && "ring-2 ring-offset-2 ring-primary scale-110"
                                            )}
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => {
                                setIsCreateBeatOpen(false)
                                setEditingBeat(null)
                                resetBeatForm()
                            }}>
                                Cancel
                            </Button>
                            {editingBeat && (
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={() => {
                                        deleteBeatMutation.mutate(editingBeat.id)
                                        setEditingBeat(null)
                                        resetBeatForm()
                                    }}
                                >
                                    Delete
                                </Button>
                            )}
                            <Button type="submit" disabled={createBeatMutation.isPending || updateBeatMutation.isPending}>
                                {(createBeatMutation.isPending || updateBeatMutation.isPending) && (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                {editingBeat ? 'Update' : 'Create'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Create/Edit Act Dialog */}
            <Dialog open={isCreateActOpen || !!editingAct} onOpenChange={(open) => {
                if (!open) {
                    setIsCreateActOpen(false)
                    setEditingAct(null)
                    resetActForm()
                }
            }}>
                <DialogContent>
                    <form onSubmit={editingAct ? handleUpdateAct : handleCreateAct}>
                        <DialogHeader>
                            <DialogTitle>{editingAct ? 'Edit Act' : 'Create New Act'}</DialogTitle>
                            <DialogDescription>
                                {editingAct
                                    ? 'Update this act\'s name and color.'
                                    : 'Create a new act for your story structure. You can name it anything — "Act 1", "Setup", "The Journey", etc.'}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="actName">Name</Label>
                                <Input
                                    id="actName"
                                    placeholder="e.g., Act 1, Setup, Rising Action..."
                                    value={newActName}
                                    onChange={(e) => setNewActName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Color</Label>
                                <div className="flex gap-2 flex-wrap">
                                    {ACT_COLORS.map((color) => (
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() => setNewActColor(color)}
                                            className={cn(
                                                "w-8 h-8 rounded-lg transition-transform",
                                                newActColor === color && "ring-2 ring-offset-2 ring-primary scale-110"
                                            )}
                                            style={{ backgroundColor: color }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => {
                                setIsCreateActOpen(false)
                                setEditingAct(null)
                                resetActForm()
                            }}>
                                Cancel
                            </Button>
                            {editingAct && (
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={() => {
                                        handleDeleteAct(editingAct.id)
                                        setEditingAct(null)
                                        resetActForm()
                                    }}
                                >
                                    Delete Act
                                </Button>
                            )}
                            <Button type="submit" disabled={updateBoardMutation.isPending}>
                                {updateBoardMutation.isPending && (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                {editingAct ? 'Update' : 'Create'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    )
}

